import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authClient } from '../utils/amplifyClient';
import { useUserContext } from '../contexts/UserContext';
import { createCompanyNote } from '../graphql/mutations';
import '../css/LockedModulePanel.css';



interface LockedModulePanelProps {
    moduleName: string;
    description: string;
    features: string[];
    /** The module enrollment key on the company (e.g., 'pmModuleEnrollment') */
    moduleKey?: string;
    /** Override: force admin or staff view */
    forceRole?: 'admin' | 'staff';
}

/**
 * Locked Module Panel — shown when a module is not activated.
 * Admin sees "Activate" CTA → navigates to settings/billing.
 * Staff sees "Request Access from Administrator" → posts a CompanyNote.
 */
export const LockedModulePanel = ({
    moduleName,
    description,
    features,
    forceRole,
}: LockedModulePanelProps) => {
    const navigate = useNavigate();
    const { company, companyOwner, companyUser } = useUserContext();

    const [requestSent, setRequestSent] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const isAdmin = forceRole
        ? forceRole === 'admin'
        : !!companyOwner;

    const adminName = companyOwner?.name || 'Your Company Admin';
    const companyId = company?.id || companyOwner?.companyId || '';

    const handleActivate = () => {
        navigate('/settings');
    };

    const handleRequestAccess = async () => {
        if (requestSent || isSending) return;
        setIsSending(true);

        try {
            const userName = companyUser?.name || 'A team member';
            await authClient.graphql({
                query: createCompanyNote,
                variables: {
                    input: {
                        companyId,
                        authorName: userName,
                        content: `[Module Request] ${userName} has requested access to the ${moduleName} module. Navigate to Settings & Billing to activate this module for your company.`,
                    },
                },
            });
            setRequestSent(true);
        } catch (error) {
            console.error('Error sending access request:', error);
            setRequestSent(true);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="locked-module-panel">
            <div className="locked-module-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <rect width="48" height="48" rx="24" fill="#f1f5f9" />
                    <path d="M24 16C21.79 16 20 17.79 20 20V22H18V32H30V22H28V20C28 17.79 26.21 16 24 16ZM24 18C25.1 18 26 18.9 26 20V22H22V20C22 18.9 22.9 18 24 18ZM24 26C25.1 26 26 26.9 26 28C26 29.1 25.1 30 24 30C22.9 30 22 29.1 22 28C22 26.9 22.9 26 24 26Z" fill="#94a3b8" />
                </svg>
            </div>

            <h2 className="locked-module-title">{moduleName} is not activated</h2>

            <p className="locked-module-description">
                {isAdmin
                    ? description
                    : `Your administrator hasn't activated this module yet. Request access and they'll be notified.`
                }
            </p>

            <ul className="locked-module-features">
                {features.map((feature, idx) => (
                    <li key={idx}>
                        <span className="locked-module-check">✓</span>
                        {feature}
                    </li>
                ))}
            </ul>

            {isAdmin ? (
                <>
                    <button className="locked-module-cta admin" onClick={handleActivate}>
                        🔑 Activate {moduleName} Module
                    </button>
                    <p className="locked-module-helper">
                        This will activate the module for your entire company.
                    </p>
                </>
            ) : (
                <>
                    <button
                        className={`locked-module-cta staff ${requestSent ? 'sent' : ''}`}
                        onClick={handleRequestAccess}
                        disabled={requestSent || isSending}
                    >
                        {isSending ? 'Sending...' : requestSent ? '✓ Request Sent' : 'Request Access from Administrator'}
                    </button>
                    <p className="locked-module-helper">
                        <strong>{adminName}</strong> (Company Admin) will be notified of your request.
                    </p>
                </>
            )}
        </div>
    );
};
